import { useMutation, useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/modules/shared/utils/API'
import { onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { trainingMaterialTitleFormatter } from '@tabeeb/modules/shared/utils/text'

import { queryClient } from '@tabeeb/services/queryClient'
import { ArticlesQueryKeys } from '../queries'

export interface UseContentTrainingMaterialProps {
  id?: number
  onMarkedAsRead?: () => void
  onError?: (error: unknown) => void
}

export const useContentTrainingMaterial = ({ id, onMarkedAsRead, onError }: UseContentTrainingMaterialProps) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [ArticlesQueryKeys.ContentTrainingMaterial, id],
    queryFn: () => api.contentTrainingMaterials.getAsync(id!),
    enabled: !!id,
    onError,
  })

  const markAsReadMutation = useMutation({
    mutationFn: () => {
      return api.contentTrainingMaterials.markAsReadAsync({
        ContentTrainingMaterialId: id!,
      })
    },
    onError: () => {
      dispatch(
        onAddSuccessNotification({ message: trainingMaterialTitleFormatter.format('Training material marked as read') })
      )
    },
    onSuccess: () => {
      onMarkedAsRead?.()

      queryClient.setQueryData<number>(
        [ArticlesQueryKeys.UnreadTrainingMaterialsCount, query.data?.ContentId],
        (prevCount) => {
          if (!prevCount) {
            return 0
          }

          return Math.max(prevCount - 1, 0)
        }
      )

      query.refetch()
    },
  })

  return {
    loading: query.isLoading,
    contentTrainingMaterial: query.data,
    onReload: query.refetch,
    isSubmitInProgress: markAsReadMutation.isLoading,
    onMarkAsRead: markAsReadMutation.mutate,
  }
}

export default useContentTrainingMaterial
